/**
 * Terms of Service, Privacy Policy and Imprint. The texts are templates in English, filled with the operator's
 * details (LEGAL_* in .env); the pages exist only when LEGAL_NAME is set. Have them checked by a lawyer.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { usePublicConfig } from './Auth';
import { t } from '../i18n';

function LegalShell({ title, children }: { title: string; children: ReactNode }) {
  const cfg = usePublicConfig();
  return (
    <div className="landing legal">
      <header className="landing-nav">
        <Link to="/" className="landing-brand">
          {cfg.data?.app_name ?? ''}
        </Link>
        <nav>
          <Link to="/login">{t('Log in')}</Link>
        </nav>
      </header>
      <article className="legal-text">
        <h1>{title}</h1>
        {children}
      </article>
    </div>
  );
}

function useLegal() {
  const cfg = usePublicConfig();
  const c = cfg.data;
  if (!c) return null;
  return { ...c, l: c.legal };
}

export function LegalPage({ page }: { page: 'terms' | 'privacy' | 'imprint' }) {
  const c = useLegal();
  if (!c) return <div className="fullpage subtle">{t('Loading…')}</div>;
  const l = c.l;
  if (!l) {
    return (
      <LegalShell title={t('Not available')}>
        <p>{t('This page has not been set up yet.')}</p>
      </LegalShell>
    );
  }
  const app = c.app_name;
  const operator = (
    <>
      {l.name}
      {l.address && `, ${l.address}`}
      {l.email && (
        <>
          , <a href={`mailto:${l.email}`}>{l.email}</a>
        </>
      )}
    </>
  );

  if (page === 'imprint') {
    return (
      <LegalShell title="Imprint">
        <p>
          <strong>{l.name}</strong>
          {l.address && (
            <>
              <br />
              {l.address}
            </>
          )}
        </p>
        {l.registration && <p>{l.registration}</p>}
        {l.email && (
          <p>
            E-mail: <a href={`mailto:${l.email}`}>{l.email}</a>
          </p>
        )}
        <p>Responsible for the content of this site: {l.name}.</p>
      </LegalShell>
    );
  }

  if (page === 'privacy') {
    return (
      <LegalShell title="Privacy Policy">
        <h2>Who is responsible</h2>
        <p>{operator} (“we”) operates {app} and is responsible for the personal data processed in it.</p>
        <h2>What we process</h2>
        <ul>
          <li>Account data: username, name, e-mail address, password (stored only as a hash), language and notification settings.</li>
          <li>Content of your teams: projects, issues, notes, attachments and their history. Team administrators decide what goes into their team.</li>
          <li>Technical data: IP address and request details in server logs, a session cookie while you are logged in.</li>
          <li>If you connect them: your Telegram chat ID, and the commit data GitHub or GitLab send to a team’s webhook.</li>
        </ul>
        <h2>Why</h2>
        <ul>
          <li>To provide the service you signed up for (contract, Art. 6(1)(b) GDPR), including confirmations, invitations and notifications.</li>
          <li>To keep the service secure and prevent abuse (legitimate interest, Art. 6(1)(f) GDPR).</li>
        </ul>
        <h2>Cookies and tracking</h2>
        <p>We use one cookie that keeps you logged in, and your browser stores your language choice. There is no advertising, no analytics and no tracking by third parties.</p>
        <h2>Who else receives data</h2>
        <ul>
          <li>Our hosting{l.hosting ? ` (${l.hosting})` : ''}, where the data and backups are stored.</li>
          <li>The e-mail service that delivers our e-mails.</li>
          <li>Telegram, only if you connect it, for the messages sent to you.</li>
        </ul>
        <p>We do not sell personal data. If data is processed outside the EU, we use the safeguards the GDPR requires, such as standard contractual clauses.</p>
        <h2>How long</h2>
        <p>
          As long as your account or team exists. When you delete your account, your name, e-mail and password are removed; issues and notes you wrote stay in their teams as
          “Deleted user”. A deleted team is removed with all its content. Backups are overwritten within 30 days, server logs are kept for at most 30 days.
        </p>
        <h2>Your rights</h2>
        <p>
          You can access, correct, export and delete your data (much of it yourself on My Account and Manage → Team), restrict or object to processing, and complain to a data
          protection authority. Write to us: {l.email ? <a href={`mailto:${l.email}`}>{l.email}</a> : l.name}.
        </p>
      </LegalShell>
    );
  }

  return (
    <LegalShell title="Terms of Service">
      <p>These terms apply to the use of {app}, operated by {operator}.</p>
      <h2>1. Accounts and teams</h2>
      <p>
        You need an account with a correct e-mail address. Keep your password secret; you are responsible for what is done with your account. The administrators of a team decide
        who belongs to it and what members may do.
      </p>
      <h2>2. Internal use</h2>
      <p>{app} is provided as an internal company tool. Access may be limited to invited or authorized users.</p>
      <h2>3. Your content</h2>
      <p>
        You keep all rights to what you put into {app}. You allow us to store and process it only to provide the service. You are responsible for your content and must not upload
        anything illegal or anything you have no right to share.
      </p>
      <h2>4. Fair use</h2>
      <p>Do not attack or overload the service, send spam through it or use it against others. We may suspend accounts or teams that do.</p>
      <h2>5. Availability and backups</h2>
      <p>
        We work to keep {app} available and back it up daily, but we cannot promise uninterrupted service. You can export your team’s data at any time on Manage → Team.
      </p>
      <h2>6. Liability</h2>
      <p>
        We are liable without limit for intent and gross negligence and where the law does not allow limits. Otherwise liability is limited to foreseeable damage caused by a
        breach of essential obligations, to the extent permitted by law.
      </p>
      <h2>7. Ending</h2>
      <p>
        You can delete your team or account at any time. If we stop offering {app}, we tell you at least 30 days in advance so you can export your data.
      </p>
      <h2>8. Changes</h2>
      <p>We announce changes to these terms by e-mail at least 30 days before they apply. If you do not agree, you can delete your account before then.</p>
      <h2>9. Law</h2>
      <p>The law of the country where the operator is registered applies, unless mandatory consumer protection law of your country says otherwise.</p>
    </LegalShell>
  );
}
